import React, { Fragment } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { getNetworkAlertsData, getNetworkAlertsInitials, acknowledgeAlerts } from '../../redux/actions/networkActions';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import Content from '../../components/Content';
import Table from '../../components/Table';
// import Button from '../../components/Button';
import TextInput from '../../components/TextInput';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';

import NetworkAlertsStyles from './NetworkAlerts.module.css';

const FRONTEND_NETWORK_ALERTS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	TABLE_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	FILTER_INVALID: ErrorCodes.API_INVALID_PARAMETER,
	NO_WATCHERS_AVAILABLE: ErrorCodes.RESOURCE_NOT_FOUND,
	CSV_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
	PDF_EXPORT_FAILED: ErrorCodes.FILE_PROCESSING_FAILED,
};

const NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY = 'network_hidden_alert_keys_v1';
const NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY = 'network_advanced_hide_rules_v1';
const PRESET_LAST_7_DAYS_TIMEFRAME = '7';
const EMPTY_ADVANCED_HIDE_RULES = {
	gidSidRev: [],
	messageText: [],
	srcIpPort: [],
};

// The API keeps acknowledgement state and real alert IDs in trailing cells.
// IDs are visible and searchable; acknowledgement state only styles the row.
const ALERT_META_COLS = ['Acked', 'Id'];
const ALERT_HIDDEN_COLS = ['Acked'];
const alertColumnLabel = (col) => col === 'Id' ? 'Alert ID' : col;

// Keep the original hide/selection key even though IDs are now displayed.
// Excluding both trailing cells preserves saved hides and stable selection
// when acknowledgement state changes.
const getNetworkAlertHideKey = (alertRow) => {
	const cells = alertRow || [];
	const visible = cells.length > ALERT_META_COLS.length
		? cells.slice(0, cells.length - ALERT_META_COLS.length)
		: cells;
	return visible.map((cell) => String(cell ?? '')).join('||');
};

const normalizeAdvancedHideRuleValue = (ruleType, rawValue) => {
	const value = String(rawValue ?? '').trim();
	if (!value) return '';

	if (ruleType === 'gidSidRev') {
		return value.replace(/[[\]\s]/g, '');
	}

	if (ruleType === 'srcIpPort') {
		return value.toLowerCase();
	}

	if (ruleType === 'messageText') {
		return value.toLowerCase();
	}

	return value;
};

const parseNetworkAlertDetails = (alertText) => {
	const safeAlertText = String(alertText ?? '');
	if (!safeAlertText) {
		return {
			gidSidRev: '',
			messageText: '',
			srcIpPort: '',
		};
	}

	const gidSidRevMatch = safeAlertText.match(/\[(\d+:\d+:\d+)\]/);
	const messageMatch = safeAlertText.match(/\[\d+:\d+:\d+\]\s*(.*?)\s*\[\*\*\]/);
	const srcIpPortMatch = safeAlertText.match(/\}\s*([^\s]+)\s*->/);

	return {
		gidSidRev: gidSidRevMatch ? gidSidRevMatch[1] : '',
		messageText: messageMatch ? messageMatch[1] : safeAlertText,
		srcIpPort: srcIpPortMatch ? srcIpPortMatch[1] : '',
	};
};

const readAdvancedHideRulesFromStorage = (storageKey) => {
	if (typeof window === 'undefined') return { ...EMPTY_ADVANCED_HIDE_RULES };

	try {
		const rawValue = window.localStorage.getItem(storageKey);
		if (!rawValue) return { ...EMPTY_ADVANCED_HIDE_RULES };

		const parsed = JSON.parse(rawValue);
		if (!parsed || typeof parsed !== 'object') return { ...EMPTY_ADVANCED_HIDE_RULES };

		const normalizeArray = (values) =>
			Array.from(
				new Set(
					(Array.isArray(values) ? values : [])
						.map((value) => String(value ?? '').trim())
						.filter(Boolean)
				)
			);

		return {
			gidSidRev: normalizeArray(parsed.gidSidRev),
			messageText: normalizeArray(parsed.messageText),
			srcIpPort: normalizeArray(parsed.srcIpPort),
		};
	} catch (error) {
		return { ...EMPTY_ADVANCED_HIDE_RULES };
	}
};

const readSetFromStorage = (storageKey) => {
	if (typeof window === 'undefined') return new Set();
	try {
		const rawValue = window.localStorage.getItem(storageKey);
		if (!rawValue) return new Set();
		const parsed = JSON.parse(rawValue);
		if (!Array.isArray(parsed)) return new Set();
		return new Set(parsed);
	} catch (error) {
		return new Set();
	}
};

const writeSetToStorage = (storageKey, valuesSet) => {
	if (typeof window === 'undefined') return;
	try {
		if (!valuesSet || valuesSet.size === 0) {
			window.localStorage.removeItem(storageKey);
			return;
		}
		window.localStorage.setItem(storageKey, JSON.stringify(Array.from(valuesSet)));
	} catch (error) {
		// Ignore storage write errors to avoid breaking UI behavior.
	}
};

const writeAdvancedHideRulesToStorage = (storageKey, advancedHideRules) => {
	if (typeof window === 'undefined') return;

	try {
		const hasRules =
			(Array.isArray(advancedHideRules?.gidSidRev) && advancedHideRules.gidSidRev.length > 0) ||
			(Array.isArray(advancedHideRules?.messageText) && advancedHideRules.messageText.length > 0) ||
			(Array.isArray(advancedHideRules?.srcIpPort) && advancedHideRules.srcIpPort.length > 0);

		if (!hasRules) {
			window.localStorage.removeItem(storageKey);
			return;
		}

		window.localStorage.setItem(storageKey, JSON.stringify(advancedHideRules));
	} catch (error) {
		// Ignore storage write errors to avoid breaking UI behavior.
	}
};

const buildNetworkAlertsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const normalizeWatcherValue = (watcherValue) => String(watcherValue ?? '').trim().toLowerCase();

const normalizeWatcherCore = (watcherValue) => normalizeWatcherValue(watcherValue).replace(/^watcher/i, '');

const resolveWatcherIdFromOptions = (watcherOptions, requestedWatcherId) => {
	if (!Array.isArray(watcherOptions) || watcherOptions.length === 0) return '';

	const normalizedRequestedId = normalizeWatcherValue(requestedWatcherId);
	const normalizedRequestedCore = normalizeWatcherCore(requestedWatcherId);

	// Exact/case-insensitive match on watcher id.
	let matchedWatcherOption = watcherOptions.find(
		(watcherOption) => normalizeWatcherValue(watcherOption?.[0]) === normalizedRequestedId
	);

	// Fallback: match by common "Watcher" prefix differences (e.g., "Watcher65" vs "65").
	if (!matchedWatcherOption && normalizedRequestedCore) {
		matchedWatcherOption = watcherOptions.find(
			(watcherOption) => normalizeWatcherCore(watcherOption?.[0]) === normalizedRequestedCore
		);
	}

	return matchedWatcherOption ? String(matchedWatcherOption[0] ?? '').trim() : '';
};

const NetworkAlertsScreen = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	
	// Get user info from Redux store
	const userLogin = useSelector((state) => state.userLogin);
	const userCompany = userLogin?.userInfo?.session?.company || 'None';
	const isFlcUser = userCompany === 'FLC';
	
	// Add debugging
	console.log('User Company:', userCompany);
	console.log('Is FLC User:', isFlcUser);
	
		const [ selectedWatcher, setSelectedWatcher ] = React.useState('');
		const [ availableWatchers, setAvailableWatchers ] = React.useState([]);
	
	const [ dataLoading, setDataLoading ] = React.useState(false);
	const [ networkDataInitials, setNetworkDataInitials ] = React.useState({});
	const [ networkData, setNetworkData ] = React.useState({});
	const [ networkTotalRows, setNetworkTotalRows ] = React.useState(0);
	const [ networkRows, setNetworkRows ] = React.useState([]);
	const [ hasError, setHasError ] = React.useState(false);
	const [ supportErrorMessage, setSupportErrorMessage ] = React.useState('');
	const [ copiedSupportError, setCopiedSupportError ] = React.useState(false);
	
	// State for note modal
	const [ showNoteModal, setShowNoteModal ] = React.useState(false);
	const [ selectedAlert, setSelectedAlert ] = React.useState(null);

	// State for search functionality
	const [ searchQuery, setSearchQuery ] = React.useState('');
	const [ filteredData, setFilteredData ] = React.useState([]);
	const [ selectedNetworkAlertKeys, setSelectedNetworkAlertKeys ] = React.useState(new Set());
	// Bumped after an acknowledge so the data effect re-fetches (acked rows come
	// back greyed). Kept separate from the filter inputs so acking doesn't reset them.
	const [ refreshNonce, setRefreshNonce ] = React.useState(0);
	// Collapse identical alerts (same watcher + message) into one row with an
	// occurrence count. Default on — it tames a per-connection flood.
	const [ groupDuplicates, setGroupDuplicates ] = React.useState(true);
	const [ ackInFlight, setAckInFlight ] = React.useState(false);
	// Note modal for Ack / Ack & Allow: { rows, allow, count } while open.
	const [ ackNotePrompt, setAckNotePrompt ] = React.useState(null);
	const [ ackNoteText, setAckNoteText ] = React.useState('');
	const [ lastSelectedNetworkAlertKey, setLastSelectedNetworkAlertKey ] = React.useState(null);
	const [ hiddenNetworkAlertKeys, setHiddenNetworkAlertKeys ] = React.useState(() =>
		readSetFromStorage(NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY)
	);
	const [ advancedHideRules, setAdvancedHideRules ] = React.useState(() =>
		readAdvancedHideRulesFromStorage(NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY)
	);
	const [ showAdvancedHideOptions, setShowAdvancedHideOptions ] = React.useState(false);
	const [ advancedHideGidSidRevInput, setAdvancedHideGidSidRevInput ] = React.useState('');
	const [ advancedHideMessageTextInput, setAdvancedHideMessageTextInput ] = React.useState('');
	const [ advancedHideSrcIpPortInput, setAdvancedHideSrcIpPortInput ] = React.useState('');
	const [ hasAppliedWatcherQuery, setHasAppliedWatcherQuery ] = React.useState(false);

	const [ pageNumber, setPageNumber ] = React.useState(1);
	const pageLimit = 15;

	// Add new state for date range
	const [dateRangeMode, setDateRangeMode] = React.useState('preset'); // 'preset' or 'custom'
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');

	const hiddenAlertCount = hiddenNetworkAlertKeys.size;
	const advancedHideRuleCount =
		advancedHideRules.gidSidRev.length +
		advancedHideRules.messageText.length +
		advancedHideRules.srcIpPort.length;
	const hasAdvancedHideRules = advancedHideRuleCount > 0;
	const rawNetworkRowsCount = Array.isArray(networkData?.NetRows) ? networkData.NetRows.length : 0;
	const allFilteredSelected =
		filteredData.length > 0 &&
		filteredData.every((row) => selectedNetworkAlertKeys.has(getNetworkAlertHideKey(row)));
	const normalizedAdvancedHideRules = React.useMemo(() => ({
		gidSidRev: advancedHideRules.gidSidRev
			.map((rule) => normalizeAdvancedHideRuleValue('gidSidRev', rule))
			.filter(Boolean),
		messageText: advancedHideRules.messageText
			.map((rule) => normalizeAdvancedHideRuleValue('messageText', rule))
			.filter(Boolean),
		srcIpPort: advancedHideRules.srcIpPort
			.map((rule) => normalizeAdvancedHideRuleValue('srcIpPort', rule))
			.filter(Boolean),
	}), [advancedHideRules]);

	const setNetworkAlertsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setHasError(true);
		setSupportErrorMessage(buildNetworkAlertsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearNetworkAlertsSupportError = React.useCallback(() => {
		setHasError(false);
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);
	
	// Fetch available companies for FLC users from the backend
	React.useEffect(() => {
		const fetchAvailableData = async () => {
			if (isFlcUser && networkDataInitials && networkDataInitials.availableWatchers) {
				setAvailableWatchers(networkDataInitials.availableWatchers);
			}
		};

		fetchAvailableData();
	}, [isFlcUser, networkDataInitials]);

	React.useEffect(
		() => {
			const getData = async () => {
				setDataLoading(true);
				clearNetworkAlertsSupportError();

				try {
					const data = await dispatch(getNetworkAlertsInitials());
					console.log('Network Data Initials:', data); // Debug log
					if (data && data.status) {
						setNetworkDataInitials(data);
						// Set available watchers for FLC users
						if (isFlcUser && data.availableWatchers) {
							setAvailableWatchers(data.availableWatchers);
						}

						const hasRegularWatchers =
							Array.isArray(data.watchers) &&
							data.watchers.length > 0 &&
							Array.isArray(data.watchers[0]) &&
							data.watchers[0].length >= 2;
						const hasFlcWatchers =
							Array.isArray(data.availableWatchers) &&
							data.availableWatchers.length > 0 &&
							Array.isArray(data.availableWatchers[0]) &&
							data.availableWatchers[0].length >= 2;
						const hasAnyWatchers = isFlcUser ? hasFlcWatchers || hasRegularWatchers : hasRegularWatchers;

						if (!hasAnyWatchers) {
							setNetworkAlertsSupportError(
								FRONTEND_NETWORK_ALERTS_ERROR_CODES.NO_WATCHERS_AVAILABLE,
								'Network Alerts Page / No Watchers Available',
								'No watchers are configured for network alerts data'
							);
						} else {
							clearNetworkAlertsSupportError();
						}
					} else {
						setNetworkAlertsSupportError(
							FRONTEND_NETWORK_ALERTS_ERROR_CODES.INITIAL_LOAD_FAILED,
							'Network Alerts Page / Initial Data Load Failed',
							data?.msg || (typeof data === 'string' ? data : 'Network alerts initial request did not return success')
						);
					}
				} catch (error) {
					setNetworkAlertsSupportError(
						FRONTEND_NETWORK_ALERTS_ERROR_CODES.INITIAL_LOAD_FAILED,
						'Network Alerts Page / Initial Data Request Error',
						error?.message || String(error)
					);
				}
				setDataLoading(false);
			};

			getData();
		},
		[ dispatch, isFlcUser, clearNetworkAlertsSupportError, setNetworkAlertsSupportError ]
	);

	React.useEffect(
		() => {
			// Guard against a stale response clobbering a newer selection: when the
			// watcher (or date range) changes, this effect re-runs and its cleanup
			// sets `cancelled`, so the in-flight request for the PREVIOUS selection
			// (e.g. the initial "all watchers" load) is ignored when it finally
			// resolves. Without this, a slow "all" response landing after the
			// Watcher149 response repainted the table with every watcher's alerts.
			let cancelled = false;
			const getData = async () => {
				if (!selectedWatcher) return;

				const isCustomMode = dateRangeMode === 'custom';
				const hasCompleteCustomDateRange = Boolean(startDate && endDate);
				if (isCustomMode && !hasCompleteCustomDateRange) {
					// Wait until both custom dates are selected before reloading the table.
					return;
				}
				
				setDataLoading(true);
				clearNetworkAlertsSupportError();
				// Clear the previous watcher's rows immediately so they can never
				// linger behind (or blend with) the newly selected watcher while the
				// next request is in flight.
				setNetworkData({});
				const formData = new FormData();

				// Add watcher selection logic...
				if (selectedWatcher) {
					formData.append('watchers', selectedWatcher);
				}
				formData.append('group', groupDuplicates ? '1' : '0');

				// Handle date range vs preset timeframe
				if (isCustomMode) {
					formData.append('date_range_mode', 'custom');
					formData.append('start_date', startDate); // YYYY-MM-DD format
					formData.append('end_date', endDate);
				} else {
					formData.append('date_range_mode', 'preset');
					formData.append('timeframe', PRESET_LAST_7_DAYS_TIMEFRAME);
				}

				const data = await dispatch(getNetworkAlertsData(formData));
				if (cancelled) return;   // a newer watcher/date selection superseded this request
				if (data && data.status) {
					setNetworkData(data);
					clearNetworkAlertsSupportError();
				} else {
					setNetworkData({});
					const isCustomMissingDate = dateRangeMode === 'custom' && (!startDate || !endDate);
					setNetworkAlertsSupportError(
						isCustomMissingDate
							? FRONTEND_NETWORK_ALERTS_ERROR_CODES.FILTER_INVALID
							: FRONTEND_NETWORK_ALERTS_ERROR_CODES.TABLE_LOAD_FAILED,
						isCustomMissingDate
							? 'Network Alerts Page / Custom Date Filter Incomplete'
							: 'Network Alerts Page / Table Data Load Failed',
						data?.msg || 'Network alerts table request did not return success'
					);
				}
				setDataLoading(false);
				setPageNumber(1);
			};

			getData();
			return () => { cancelled = true; };
		},
		// Only the actual filter inputs — NOT the error-setter callbacks, which are
		// recreated on every render and were re-running this effect (and, with the
		// stale-response guard, cancelling the in-flight fetch) on the component's
		// constant re-renders, so the newly selected watcher's data never landed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, selectedWatcher, dateRangeMode, startDate, endDate, refreshNonce, groupDuplicates]
	);

	React.useEffect(
		() => {
			setHasAppliedWatcherQuery(false);
		},
		[ location.search ]
	);

	// Deep-link from the dashboard: ?q=<ip|host> pre-fills the search so
	// Investigate lands filtered to that event's rows, not the whole list.
	React.useEffect(
		() => {
			const q = new URLSearchParams(location.search).get('q');
			if (q) setSearchQuery(q);
		},
		[ location.search ]
	);

	React.useEffect(
		() => {
			if (hasAppliedWatcherQuery) return;

			const watcherIdFromQuery = new URLSearchParams(location.search).get('watcherId');
			if (!watcherIdFromQuery) {
				setHasAppliedWatcherQuery(true);
				return;
			}

			const normalizedWatcherIdFromQuery = String(watcherIdFromQuery).trim();
			if (!normalizedWatcherIdFromQuery) {
				setHasAppliedWatcherQuery(true);
				return;
			}

			const watcherOptions = isFlcUser
				? (
					Array.isArray(availableWatchers) && availableWatchers.length > 0
						? availableWatchers
						: (Array.isArray(networkDataInitials?.watchers) ? networkDataInitials.watchers : [])
				)
				: (Array.isArray(networkDataInitials?.watchers) ? networkDataInitials.watchers : []);

			if (!Array.isArray(watcherOptions) || watcherOptions.length === 0) {
				// Wait until watcher options have been loaded.
				return;
			}

			const resolvedWatcherId = resolveWatcherIdFromOptions(watcherOptions, normalizedWatcherIdFromQuery);
			if (resolvedWatcherId) {
				setSelectedWatcher(resolvedWatcherId);
				setSelectedNetworkAlertKeys(new Set());
				setPageNumber(1);
			}

			setHasAppliedWatcherQuery(true);
		},
		[
			hasAppliedWatcherQuery,
			location.search,
			isFlcUser,
			availableWatchers,
			networkDataInitials,
		]
	);

	// No auto-select of "All Watchers": the page renders with the dropdown
	// populated but no data until the user picks a watcher. Query-param /
	// deep-link preselection (the effect above) still runs when present.

	const filterRowsBySearchQuery = React.useCallback((rows) => {
		if (!Array.isArray(rows)) return [];

		const hasSearchQuery = Boolean(searchQuery.trim());
		const query = searchQuery.toLowerCase().trim();
		const netCols = Array.isArray(networkData?.NetCols) ? networkData.NetCols : [];
		const alertColumnIndex = netCols.findIndex((col) => col === 'Alert');

		const matchesAdvancedHideRule = (row) => {
			if (!hasAdvancedHideRules) return false;

			const alertValue = alertColumnIndex !== -1 ? row[alertColumnIndex] : row[2];
			const parsedAlert = parseNetworkAlertDetails(alertValue);

			const rowGidSidRev = normalizeAdvancedHideRuleValue('gidSidRev', parsedAlert.gidSidRev);
			if (rowGidSidRev && normalizedAdvancedHideRules.gidSidRev.includes(rowGidSidRev)) {
				return true;
			}

			const rowSrcIpPort = normalizeAdvancedHideRuleValue('srcIpPort', parsedAlert.srcIpPort);
			if (rowSrcIpPort && normalizedAdvancedHideRules.srcIpPort.includes(rowSrcIpPort)) {
				return true;
			}

			const rowMessageText = normalizeAdvancedHideRuleValue('messageText', parsedAlert.messageText || alertValue);
			if (
				rowMessageText &&
				normalizedAdvancedHideRules.messageText.some((rule) => rowMessageText.includes(rule))
			) {
				return true;
			}

			return false;
		};

		return rows.filter((row) => {
			const alertKey = getNetworkAlertHideKey(row);
			if (hiddenNetworkAlertKeys.has(alertKey)) return false;
			if (matchesAdvancedHideRule(row)) return false;

			if (!hasSearchQuery) return true;

			// Include real IDs, including every ID in a grouped row.
			const searchableColumns = netCols
				.map((col, index) => {
					if (col !== 'Note' && !ALERT_HIDDEN_COLS.includes(col)) {
						return row[index];
					}
					return null;
				})
				.filter((cell) => cell != null && String(cell).trim() !== '');

			return searchableColumns.some((cell) =>
				cell.toString().toLowerCase().includes(query)
			);
		});
	}, [
		searchQuery,
		networkData?.NetCols,
		hiddenNetworkAlertKeys,
		hasAdvancedHideRules,
		normalizedAdvancedHideRules
	]);

	React.useEffect(
		() => {
			if (networkData && Object.keys(networkData).length > 0 && Array.isArray(networkData.NetRows)) {
				const filteredRows = filterRowsBySearchQuery(networkData.NetRows);
				const totalRows = filteredRows.length;
				const totalPages = totalRows > 0 ? Math.ceil(totalRows / pageLimit) : 1;
				const boundedPageNumber = Math.min(Math.max(pageNumber, 1), totalPages);
				const pageStartIndex = (boundedPageNumber - 1) * pageLimit;
				const pageEndIndex = pageStartIndex + pageLimit;

				if (boundedPageNumber !== pageNumber) {
					setPageNumber(boundedPageNumber);
				}

				setFilteredData(filteredRows);
				setNetworkTotalRows(totalRows);
				setNetworkRows(filteredRows.slice(pageStartIndex, pageEndIndex));
			} else {
				setNetworkTotalRows(0);
				setNetworkRows([]);
				setFilteredData([]);
				if (pageNumber !== 1) {
					setPageNumber(1);
				}
			}
		},
		[ networkData, filterRowsBySearchQuery, pageNumber ]
	);

	React.useEffect(() => {
		setSelectedNetworkAlertKeys((prevSelected) => {
			if (prevSelected.size === 0) return prevSelected;

			const visibleAlertKeys = new Set(filteredData.map((row) => getNetworkAlertHideKey(row)));
			let changed = false;
			const nextSelected = new Set();

			prevSelected.forEach((alertKey) => {
				if (visibleAlertKeys.has(alertKey)) {
					nextSelected.add(alertKey);
				} else {
					changed = true;
				}
			});

			return changed ? nextSelected : prevSelected;
		});
	}, [filteredData]);

	React.useEffect(() => {
		writeSetToStorage(NETWORK_HIDDEN_ALERT_KEYS_STORAGE_KEY, hiddenNetworkAlertKeys);
	}, [hiddenNetworkAlertKeys]);

	React.useEffect(() => {
		writeAdvancedHideRulesToStorage(NETWORK_ADVANCED_HIDE_RULES_STORAGE_KEY, advancedHideRules);
	}, [advancedHideRules]);

	const handlePageChange = async (currentPage) => {
		setPageNumber(currentPage.selected + 1);
	};
	
	// Handle opening the note modal
	const handleViewNote = (row) => {
		// Read cells by column NAME, not fixed index — the column order can change
		// (e.g. a "Company" column was inserted), so index-based reads would break.
		const cols = Array.isArray(networkData?.NetCols) ? networkData.NetCols : [];
		const at = (name, fallback) => {
			const i = cols.findIndex((c) => c === name);
			return i !== -1 ? row[i] : row[fallback];
		};
		setSelectedAlert({
			id: row[0], // Timestamp as ID
			time: at("Time Stamp", 0),
			watcher: at("Watcher ID", 1),
			alert: at("Alert", 2),
			note: at("Note", -1) || ""
		});
		setShowNoteModal(true);
	};
	
	// Handle closing the note modal
	const handleCloseNoteModal = () => {
		setShowNoteModal(false);
		setSelectedAlert(null);
	};

	// Handle watcher selection change
	const handleWatcherChange = (e) => {
		setSelectedWatcher(e.target.value);
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
	};

		// Handle search input change
	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
		setPageNumber(1); // Reset to first page on search
		setSelectedNetworkAlertKeys(new Set());
	};

	// Update company change handler to watcher change handler for FLC users
	const handleWatcherChangeForFlc = (e) => {
		setSelectedWatcher(e.target.value);
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
	};

	const handleAlertSelectionChange = (alertKey, isSelected, isShiftPressed = false) => {
		setSelectedNetworkAlertKeys((prevSelected) => {
			const nextSelected = new Set(prevSelected);
			const selectedAlertIndex = filteredData.findIndex(
				(row) => getNetworkAlertHideKey(row) === alertKey
			);
			const lastSelectedAlertIndex = filteredData.findIndex(
				(row) => getNetworkAlertHideKey(row) === lastSelectedNetworkAlertKey
			);

			if (isShiftPressed && selectedAlertIndex !== -1 && lastSelectedAlertIndex !== -1) {
				const rangeStart = Math.min(lastSelectedAlertIndex, selectedAlertIndex);
				const rangeEnd = Math.max(lastSelectedAlertIndex, selectedAlertIndex);

				for (let rowIndex = rangeStart; rowIndex <= rangeEnd; rowIndex += 1) {
					const rangeAlertKey = getNetworkAlertHideKey(filteredData[rowIndex]);
					if (!rangeAlertKey) continue;

					if (isSelected) {
						nextSelected.add(rangeAlertKey);
					} else {
						nextSelected.delete(rangeAlertKey);
					}
				}
			} else if (isSelected) {
				nextSelected.add(alertKey);
			} else {
				nextSelected.delete(alertKey);
			}
			return nextSelected;
		});
		setLastSelectedNetworkAlertKey(alertKey);
	};

	const handleToggleSelectAll = () => {
		setSelectedNetworkAlertKeys((prevSelected) => {
			const nextSelected = new Set(prevSelected);
			if (allFilteredSelected) {
				filteredData.forEach((row) => {
					nextSelected.delete(getNetworkAlertHideKey(row));
				});
				return nextSelected;
			}

			filteredData.forEach((row) => {
				nextSelected.add(getNetworkAlertHideKey(row));
			});
			return nextSelected;
		});
	};

	const handleHideSelectedAlerts = () => {
		if (selectedNetworkAlertKeys.size === 0) return;

		const selectedAlertCount = selectedNetworkAlertKeys.size;
		setHiddenNetworkAlertKeys((prevHidden) => new Set([...prevHidden, ...selectedNetworkAlertKeys]));
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
		toast.success(
			`${selectedAlertCount} alert${selectedAlertCount === 1 ? '' : 's'} hidden from Network Alerts.`
		);
	};

	// Map displayed rows → real alert ids using the trailing "Id" metadata cell.
	const alertIdsFromRows = (rows) => {
		const cols = networkData.NetCols || [];
		const idIndex = cols.indexOf('Id');
		if (idIndex === -1) return [];
		return rows.map((row) => row[idIndex]).filter((v) => v != null && String(v).trim() !== '');
	};

	const ackAlertRows = async (rows, allow = false, note = '') => {
		const ids = alertIdsFromRows(rows);
		if (ids.length === 0) {
			toast.error('No acknowledgeable alerts in the selection.');
			return;
		}
		setAckInFlight(true);
		try {
			const data = await dispatch(acknowledgeAlerts(ids, allow, note));
			if (data && data.status) {
				toast.success(data.msg || `${ids.length} alert${ids.length === 1 ? '' : 's'} acknowledged.`);
				setSelectedNetworkAlertKeys(new Set());
				setRefreshNonce((n) => n + 1); // re-fetch so acked rows come back greyed
			} else {
				toast.error((data && data.msg) || 'Could not acknowledge the alerts.');
			}
		} catch (err) {
			toast.error(err?.message || 'Could not acknowledge the alerts.');
		} finally {
			setAckInFlight(false);
		}
	};

	const selectedAlertRows = () =>
		filteredData.filter((row) => selectedNetworkAlertKeys.has(getNetworkAlertHideKey(row)));

	// Open the note modal; on confirm it calls ackAlertRows with the typed note.
	const promptAck = (rows, allow) => {
		if (!rows || rows.length === 0) return;
		setAckNoteText('');
		setAckNotePrompt({ rows, allow, count: rows.length });
	};

	const handleAckSelectedAlerts = () => {
		if (selectedNetworkAlertKeys.size === 0) return;
		promptAck(selectedAlertRows(), false);
	};

	const handleAllowSelectedAlerts = () => {
		if (selectedNetworkAlertKeys.size === 0) return;
		promptAck(selectedAlertRows(), true);
	};

	const handleAckSingleAlert = (row) => promptAck([row], false);
	const handleAllowSingleAlert = (row) => promptAck([row], true);

	const handleHideSingleAlert = (row) => {
		const alertKey = getNetworkAlertHideKey(row);
		if (!alertKey) return;

		setHiddenNetworkAlertKeys((prevHidden) => {
			if (prevHidden.has(alertKey)) return prevHidden;
			const nextHidden = new Set(prevHidden);
			nextHidden.add(alertKey);
			return nextHidden;
		});

		setSelectedNetworkAlertKeys((prevSelected) => {
			if (!prevSelected.has(alertKey)) return prevSelected;
			const nextSelected = new Set(prevSelected);
			nextSelected.delete(alertKey);
			return nextSelected;
		});

		setPageNumber(1);
		toast.success('Alert hidden from Network Alerts.');
	};

	const handleShowAllHidden = () => {
		if (hiddenNetworkAlertKeys.size === 0) return;
		setHiddenNetworkAlertKeys(new Set());
		toast.success('All hidden network alerts are visible again.');
	};

	const handleToggleAdvancedHideOptions = () => {
		setShowAdvancedHideOptions((prev) => !prev);
	};

	const getAdvancedHideRuleLabel = (ruleType) => {
		if (ruleType === 'gidSidRev') return 'GID/SID/REV';
		if (ruleType === 'messageText') return 'message text';
		return 'source IP/port';
	};

	const handleAddAdvancedHideRule = (ruleType) => {
		const inputValue =
			ruleType === 'gidSidRev'
				? advancedHideGidSidRevInput
				: ruleType === 'messageText'
					? advancedHideMessageTextInput
					: advancedHideSrcIpPortInput;
		const trimmedInputValue = String(inputValue ?? '').trim();
		const normalizedInputValue = normalizeAdvancedHideRuleValue(ruleType, trimmedInputValue);

		if (!normalizedInputValue) {
			toast.error(`Enter a ${getAdvancedHideRuleLabel(ruleType)} value to hide.`);
			return;
		}

		const existingNormalizedRules = advancedHideRules[ruleType]
			.map((ruleValue) => normalizeAdvancedHideRuleValue(ruleType, ruleValue))
			.filter(Boolean);
		if (existingNormalizedRules.includes(normalizedInputValue)) {
			toast.error(`That ${getAdvancedHideRuleLabel(ruleType)} rule already exists.`);
			return;
		}

		setAdvancedHideRules((prevRules) => {
			const nextValueToStore = ruleType === 'messageText' ? trimmedInputValue : normalizedInputValue;
			return {
				...prevRules,
				[ruleType]: [...prevRules[ruleType], nextValueToStore],
			};
		});

		if (ruleType === 'gidSidRev') setAdvancedHideGidSidRevInput('');
		if (ruleType === 'messageText') setAdvancedHideMessageTextInput('');
		if (ruleType === 'srcIpPort') setAdvancedHideSrcIpPortInput('');

		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
		toast.success(`Advanced hide enabled for ${getAdvancedHideRuleLabel(ruleType)}.`);
	};

	const handleRemoveAdvancedHideRule = (ruleType, ruleValue) => {
		setAdvancedHideRules((prevRules) => ({
			...prevRules,
			[ruleType]: prevRules[ruleType].filter((value) => value !== ruleValue),
		}));
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
		toast.success('Advanced hide rule removed.');
	};

	const handleClearAdvancedHideRules = () => {
		if (!hasAdvancedHideRules) return;
		setAdvancedHideRules({ ...EMPTY_ADVANCED_HIDE_RULES });
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
		toast.success('Advanced hide rules cleared.');
	};

	const handleResetAllHideFilters = () => {
		if (hiddenAlertCount === 0 && !hasAdvancedHideRules) return;
		setHiddenNetworkAlertKeys(new Set());
		setAdvancedHideRules({ ...EMPTY_ADVANCED_HIDE_RULES });
		setSelectedNetworkAlertKeys(new Set());
		toast.success('Hide filters reset. Network alerts are visible again.');
	};

	const exportToCSV = (tableData, columns, filename) => {
		// Include the IDs users need to trace tickets back to Watcher.
		if (Array.isArray(columns) && columns.some((c) => ALERT_HIDDEN_COLS.includes(c))) {
			const keep = columns.map((c) => !ALERT_HIDDEN_COLS.includes(c));
			columns = columns.filter((_, i) => keep[i]);
			tableData = (tableData || []).map((r) => r.filter((_, i) => keep[i]));
		}
		if (!tableData || tableData.length === 0) {
			toast.error('No data to export');
			return;
		}

		try {
			columns = columns.map(alertColumnLabel);
			// Filter out the Note column from export and get its data
			const exportColumns = columns.filter(col => col !== "Note");
			const noteColumnIndex = columns.findIndex(col => col === "Note");
			
			// Prepare data for export - exclude Note column but include note content in a separate column
			const exportData = tableData.map(row => {
				const filteredRow = row.filter((_, index) => columns[index] !== "Note");
				// Add note content as last column if note exists
				if (noteColumnIndex !== -1 && row[noteColumnIndex]) {
					filteredRow.push(row[noteColumnIndex]);
				}
				return filteredRow;
			});

			// Add Note column to headers if there are notes
			const finalColumns = [...exportColumns];
			if (noteColumnIndex !== -1) {
				finalColumns.push("Note");
			}

			// Create worksheet with headers
			const worksheet = XLSX.utils.aoa_to_sheet([finalColumns]);
			
			// Add data rows
			XLSX.utils.sheet_add_aoa(worksheet, exportData, { origin: 'A2' });
			
			// Create workbook and add the worksheet
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Network Alerts');
			
			// Generate CSV and download
			XLSX.writeFile(workbook, `${filename}.csv`);
			
			toast.success(`Exported to ${filename}.csv`);
		} catch (error) {
			const formattedError = buildNetworkAlertsSupportError(
				FRONTEND_NETWORK_ALERTS_ERROR_CODES.CSV_EXPORT_FAILED,
				'Network Alerts Page / CSV Export Failed',
				error?.message || String(error)
			);
			setNetworkAlertsSupportError(
				FRONTEND_NETWORK_ALERTS_ERROR_CODES.CSV_EXPORT_FAILED,
				'Network Alerts Page / CSV Export Failed',
				error?.message || String(error)
			);
			toast.error(formattedError);
		}
	};

	const exportToPDF = (tableData, columns, filename) => {
		// Include the IDs users need to trace tickets back to Watcher.
		if (Array.isArray(columns) && columns.some((c) => ALERT_HIDDEN_COLS.includes(c))) {
			const keep = columns.map((c) => !ALERT_HIDDEN_COLS.includes(c));
			columns = columns.filter((_, i) => keep[i]);
			tableData = (tableData || []).map((r) => r.filter((_, i) => keep[i]));
		}
		if (!tableData || tableData.length === 0) {
			toast.error('No data to export');
			return;
		}

		try {
			columns = columns.map(alertColumnLabel);
			console.log('PDF Export - Column names:', columns);
			
			const doc = new jsPDF('landscape');
			
			// Add title
			doc.setFontSize(16);
			doc.text(filename, 14, 15);
			
			// Include ALL columns including Note column
			const exportColumns = [...columns];
			const exportData = [...tableData];
			
			// Manually create a table
			const startY = 25;
			const cellPadding = 1;
			const fontSize = 7;
			const baseRowHeight = 10;
			const lineHeight = 6;
			
			// Set font size for the table
			doc.setFontSize(fontSize);
			
			// Calculate column width (give more space to Alert and Note columns)
			const pageWidth = doc.internal.pageSize.width;
			const margin = 10;
			const tableWidth = pageWidth - 2 * margin;
			
			// Special handling for column widths
			let colWidths = [];
			const noteColumnIndex = columns.findIndex(col => col === "Note");
			const alertColumnIndex = columns.findIndex(col => col === "Alert");
			
			if (noteColumnIndex !== -1 || alertColumnIndex !== -1) {
				// Allocate more space to Alert and Note columns
				const alertColumnWidth = tableWidth * 0.35; // 35% for alerts
				const noteColumnWidth = tableWidth * 0.25;  // 25% for notes
				const remainingWidth = tableWidth - (alertColumnWidth + noteColumnWidth);
				const regularColumnWidth = remainingWidth / (exportColumns.length - 2);
				
				colWidths = exportColumns.map((col, index) => {
					if (col === "Note") return noteColumnWidth;
					if (col === "Alert") return alertColumnWidth;
					return regularColumnWidth;
				});
			} else {
				// If no special columns, allocate more space to Alert column anyway
				const alertColumnWidth = tableWidth * 0.4; // 40% for alerts
				const remainingWidth = tableWidth - alertColumnWidth;
				const regularColumnWidth = remainingWidth / (exportColumns.length - 1);
				
				colWidths = exportColumns.map((col, index) => 
					col === "Alert" ? alertColumnWidth : regularColumnWidth
				);
			}
			
			// Helper function to split text into lines that fit within the given width
			const splitTextToLines = (text, maxWidth) => {
				const words = text.split(' ');
				let lines = [];
				let currentLine = '';
				
				words.forEach(word => {
					const testLine = currentLine + (currentLine ? ' ' : '') + word;
					if (doc.getTextWidth(testLine) <= maxWidth) {
						currentLine = testLine;
					} else {
						if (currentLine) lines.push(currentLine);
						currentLine = word;
					}
				});
				if (currentLine) lines.push(currentLine);
				
				return lines;
			};
			
			// Draw header
			doc.setFillColor(66, 139, 202);
			doc.setTextColor(255, 255, 255);
			doc.setFont(undefined, 'bold');
			
			doc.rect(margin, startY, tableWidth, baseRowHeight, 'F');
			
			let currentX = margin;
			exportColumns.forEach((column, i) => {
				doc.text(column.toString(), currentX + cellPadding, startY + baseRowHeight - cellPadding);
				currentX += colWidths[i];
			});
			
			// Draw rows
			doc.setTextColor(0, 0, 0);
			doc.setFont(undefined, 'normal');
			
			let y = startY + baseRowHeight;
			
			// Draw cells for each row
			exportData.forEach((row, rowIndex) => {
				// First pass: calculate the maximum number of lines needed for this row
				let maxLinesInRow = 1;
				const rowTextLines = []; // Store the lines for each cell
				
				row.forEach((cell, colIndex) => {
					if (cell !== null && cell !== undefined) {
						const text = cell.toString();
						const maxWidth = colWidths[colIndex] - (2 * cellPadding);
						const columnName = exportColumns[colIndex];
						
						if ((columnName === "Note" || columnName === "Alert") && text.length > 0) {
							const lines = splitTextToLines(text, maxWidth);
							const maxLines = columnName === "Alert" ? 4 : 3; // Allow more lines for alerts
							const finalLines = lines.length > maxLines ? 
								[...lines.slice(0, maxLines - 1), lines.slice(maxLines - 1).join(' ') + '...'] : 
								lines;
							
							rowTextLines[colIndex] = finalLines;
							maxLinesInRow = Math.max(maxLinesInRow, finalLines.length);
						} else {
							// For regular columns, check if text needs truncation
							const maxWidth = colWidths[colIndex] - (2 * cellPadding);
							if (doc.getTextWidth(text) <= maxWidth) {
								rowTextLines[colIndex] = [text];
							} else {
								let truncated = text;
								while (doc.getTextWidth(truncated + "...") > maxWidth && truncated.length > 0) {
									truncated = truncated.substring(0, truncated.length - 1);
								}
								rowTextLines[colIndex] = [truncated + "..."];
							}
						}
					} else {
						rowTextLines[colIndex] = [''];
					}
				});
				
				// Calculate row height based on maximum lines
				const rowHeight = Math.max(baseRowHeight, maxLinesInRow * lineHeight + (cellPadding * 2));
				
				// Set background color for alternating rows
				if (rowIndex % 2 === 0) {
					doc.setFillColor(240, 240, 240);
				} else {
					doc.setFillColor(255, 255, 255);
				}
				
				doc.rect(margin, y, tableWidth, rowHeight, 'F');
				
					// Second pass: draw the text lines
					currentX = margin;
					row.forEach((cell, colIndex) => {
						const lines = rowTextLines[colIndex];

						// Draw each line of text for this cell
					lines.forEach((line, lineIndex) => {
						const textY = y + cellPadding + (lineIndex + 1) * lineHeight - 1;
						doc.text(
							line,
							currentX + cellPadding,
							textY
						);
					});
					
					currentX += colWidths[colIndex];
				});
				
				y += rowHeight;
				
				// Add new page if needed
				if (y > doc.internal.pageSize.height - 20) {
					doc.addPage();
					y = 25;
				}
			});
			
			// Save PDF
			doc.save(`${filename}.pdf`);
			
			toast.success(`Exported to ${filename}.pdf`);
		} catch (error) {
			console.error('PDF generation error:', error);
			const formattedError = buildNetworkAlertsSupportError(
				FRONTEND_NETWORK_ALERTS_ERROR_CODES.PDF_EXPORT_FAILED,
				'Network Alerts Page / PDF Export Failed',
				error?.message || String(error)
			);
			setNetworkAlertsSupportError(
				FRONTEND_NETWORK_ALERTS_ERROR_CODES.PDF_EXPORT_FAILED,
				'Network Alerts Page / PDF Export Failed',
				error?.message || String(error)
			);
			toast.error(formattedError);
		}
	};

	// Add this function to get the current watcher name
	const getCurrentWatcherName = () => {
		if (!selectedWatcher) return "Network";
		
		// Check if "All Watchers" is selected
		if (selectedWatcher === 'all') return "All Watchers";
		
		// For FLC users, check availableWatchers
		if (isFlcUser && availableWatchers.length > 0) {
			const currentWatcher = availableWatchers.find(watcher => watcher[0] === selectedWatcher);
			return currentWatcher ? currentWatcher[1] : "Network";
		}
		
		// For non-FLC users, check networkDataInitials.watchers
		if (networkDataInitials && networkDataInitials.watchers && networkDataInitials.watchers.length > 0) {
			const currentWatcher = networkDataInitials.watchers.find(watcher => watcher[0] === selectedWatcher);
			return currentWatcher ? currentWatcher[1] : "Network";
		}
		
		return "Network";
	};

	// Add this function to generate the dynamic title
	const getPageTitle = () => {
		const watcherName = getCurrentWatcherName();
		return `${watcherName} Alerts`;
	};

	// Handle mode toggle
	const handleModeToggle = (mode) => {
		setDateRangeMode(mode);
		if (mode === 'preset') {
			setStartDate('');
			setEndDate('');
		}
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
	};

	const handleStartDateChange = (e) => {
		setStartDate(e.target.value);
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
	};

	const handleEndDateChange = (e) => {
		setEndDate(e.target.value);
		setSelectedNetworkAlertKeys(new Set());
		setPageNumber(1);
	};

	const filterInputStyle = React.useMemo(
		() => ({
			width: '100%',
			maxWidth: '100%',
			minWidth: 0,
			boxSizing: 'border-box',
		}),
		[]
	);

	return (
		<Content currentMenu='network' headerTitle='Alerts'>
			{dataLoading ? (
				<Loader />
			) : hasError && networkData && Object.keys(networkData).length === 0 ? (
				<>
					<NetworkError />
					{supportErrorMessage && (
						<div className={NetworkAlertsStyles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={NetworkAlertsStyles.supportErrorCode}>{supportErrorMessage}</code>
							<button
								type='button'
								className={NetworkAlertsStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
				</>
			) : (
				<Fragment>
					{hasError && supportErrorMessage && (
						<div className={NetworkAlertsStyles.supportErrorBanner}>
							<div className={NetworkAlertsStyles.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={NetworkAlertsStyles.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<button
								type='button'
								className={NetworkAlertsStyles.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
						</div>
					)}
					<div className={NetworkAlertsStyles.filterContainer}>
					{/* Watcher dropdown for FLC users */}
					{isFlcUser && (
						<div className={NetworkAlertsStyles.filterContainer_box}>
							<SelectBox 
								label="Watcher" 
								value={selectedWatcher}
								onChange={handleWatcherChangeForFlc}
								style={filterInputStyle}
							>
								<option value="">Select a Watcher</option>
								<option value="all">All Watchers</option>
								{availableWatchers.map((watcher) => (
									<option value={watcher[0]} key={watcher[0]}>
										{watcher[1]}
									</option>
								))}
							</SelectBox>
						</div>
					)}

					{/* Watcher dropdown for non-FLC users */}
					{!isFlcUser && (
						<div className={NetworkAlertsStyles.filterContainer_box}>
							<SelectBox 
								label="Watcher" 
								value={selectedWatcher}
								onChange={handleWatcherChange}
								style={filterInputStyle}
							>
								<option value="">Select a Watcher</option>
								<option value="all">All Watchers</option>
								{networkDataInitials && networkDataInitials.watchers && networkDataInitials.watchers.length > 0 ? (
									networkDataInitials.watchers.map(
										(watcher, key) =>
											watcher.length >= 2 && (
												<option value={watcher[0]} key={watcher + key}>
													{watcher[1]}
												</option>
											)
									)
								) : (
									<option value=''>No watchers found</option>
								)}
							</SelectBox>
						</div>
					)}

						{/* Date Range Mode Toggle */}
						<div className={NetworkAlertsStyles.filterContainer_box}>
							<div className={NetworkAlertsStyles.modeToggle}>
								<label>Filter:</label>
									<div className={NetworkAlertsStyles.toggleButtons}>
										<button 
											type='button'
											className={dateRangeMode === 'preset' ? NetworkAlertsStyles.active : ''}
											onClick={() => handleModeToggle('preset')}
										>
											Last 7 Days
										</button>
										<button 
											type='button'
											className={dateRangeMode === 'custom' ? NetworkAlertsStyles.active : ''}
											onClick={() => handleModeToggle('custom')}
										>
										Custom Range
									</button>
								</div>
							</div>
						</div>

						
						{dateRangeMode === 'custom' && (
							<>
								<div className={NetworkAlertsStyles.filterContainer_box}>
									<label className={NetworkAlertsStyles.dateLabel}>Start Date:</label>
									<input
										type='date'
										className={NetworkAlertsStyles.dateInput}
										value={startDate}
										onChange={handleStartDateChange}
										max={endDate || new Date().toISOString().split('T')[0]}
									/>
								</div>
								<div className={NetworkAlertsStyles.filterContainer_box}>
									<label className={NetworkAlertsStyles.dateLabel}>End Date:</label>
									<input
										type='date'
										className={NetworkAlertsStyles.dateInput}
										value={endDate}
										onChange={handleEndDateChange}
										min={startDate}
										max={new Date().toISOString().split('T')[0]}
									/>
								</div>
							</>
						)}

						{/* Search box */}
						<div className={NetworkAlertsStyles.filterContainer_box}>
							<TextInput
								label="Search Alerts"
								icon="search" 
								placeholder="Search by alert ID or text..."
								value={searchQuery}
								onChange={handleSearchChange}
								style={filterInputStyle}
							/>
						</div>
					</div>

					<div className={NetworkAlertsStyles.hide_controls}>
						<button
							type='button'
							className={`${NetworkAlertsStyles.hide_control_button} ${groupDuplicates ? NetworkAlertsStyles.ack_selected_button : ''}`}
							onClick={() => setGroupDuplicates((v) => !v)}
							title='Collapse identical alerts into one row with an occurrence count'
						>
							{groupDuplicates ? 'Grouped ✓' : 'Group Duplicates'}
						</button>
						<button
							type='button'
							className={NetworkAlertsStyles.hide_control_button}
							onClick={handleToggleSelectAll}
							disabled={filteredData.length === 0}
						>
							{allFilteredSelected ? 'Deselect All' : 'Select All'} ({filteredData.length})
						</button>
						<button
							type='button'
							className={`${NetworkAlertsStyles.hide_control_button} ${NetworkAlertsStyles.ack_selected_button}`}
							onClick={handleAckSelectedAlerts}
							disabled={selectedNetworkAlertKeys.size === 0 || ackInFlight}
						>
							{ackInFlight ? 'Acknowledging…' : `Acknowledge Selected (${selectedNetworkAlertKeys.size})`}
						</button>
						<button
							type='button'
							className={`${NetworkAlertsStyles.hide_control_button} ${NetworkAlertsStyles.allow_selected_button}`}
							onClick={handleAllowSelectedAlerts}
							disabled={selectedNetworkAlertKeys.size === 0 || ackInFlight}
							title='Acknowledge and allowlist the selected alerts so they will not alert again'
						>
							Ack &amp; Allow ({selectedNetworkAlertKeys.size})
						</button>
						<button
							type='button'
							className={`${NetworkAlertsStyles.hide_control_button} ${NetworkAlertsStyles.hide_selected_button}`}
							onClick={handleHideSelectedAlerts}
							disabled={selectedNetworkAlertKeys.size === 0}
						>
							Hide Selected ({selectedNetworkAlertKeys.size})
						</button>
						<button
							type='button'
							className={`${NetworkAlertsStyles.hide_control_button} ${NetworkAlertsStyles.show_hidden_button}`}
							onClick={handleShowAllHidden}
							disabled={hiddenAlertCount === 0}
						>
							Show Hidden ({hiddenAlertCount})
						</button>
						<button
							type='button'
							className={`${NetworkAlertsStyles.hide_control_button} ${NetworkAlertsStyles.advanced_hide_button}`}
							onClick={handleToggleAdvancedHideOptions}
						>
							{showAdvancedHideOptions
								? 'Close Advanced Hide'
								: `Advanced Hide${advancedHideRuleCount > 0 ? ` (${advancedHideRuleCount})` : ''}`}
						</button>
						{(hiddenAlertCount > 0 || hasAdvancedHideRules) && (
							<button
								type='button'
								className={`${NetworkAlertsStyles.hide_control_button} ${NetworkAlertsStyles.reset_hide_button}`}
								onClick={handleResetAllHideFilters}
							>
								Reset Hide Filters
							</button>
						)}
					</div>

					{showAdvancedHideOptions && (
						<div className={NetworkAlertsStyles.advanced_hide_panel}>
							<div className={NetworkAlertsStyles.advanced_hide_header}>
								<strong>Advanced Hide Rules</strong>
								<span className={NetworkAlertsStyles.advanced_hide_hint}>
									Hide by parsed alert fields: GID/SID/REV, message text, or source IP/port.
								</span>
							</div>

							<div className={NetworkAlertsStyles.advanced_hide_grid}>
								<div className={NetworkAlertsStyles.advanced_hide_field}>
									<label className={NetworkAlertsStyles.advanced_hide_label} htmlFor='advanced-hide-gid'>
										GID/SID/REV
									</label>
									<div className={NetworkAlertsStyles.advanced_hide_input_row}>
										<input
											id='advanced-hide-gid'
											type='text'
											value={advancedHideGidSidRevInput}
											onChange={(e) => setAdvancedHideGidSidRevInput(e.target.value)}
											className={NetworkAlertsStyles.advanced_hide_input}
											placeholder='e.g. 1:254:4'
										/>
										<button
											type='button'
											className={NetworkAlertsStyles.advanced_hide_add_button}
											onClick={() => handleAddAdvancedHideRule('gidSidRev')}
										>
											Add
										</button>
									</div>
								</div>

								<div className={NetworkAlertsStyles.advanced_hide_field}>
									<label className={NetworkAlertsStyles.advanced_hide_label} htmlFor='advanced-hide-message'>
										Message Text
									</label>
									<div className={NetworkAlertsStyles.advanced_hide_input_row}>
										<input
											id='advanced-hide-message'
											type='text'
											value={advancedHideMessageTextInput}
											onChange={(e) => setAdvancedHideMessageTextInput(e.target.value)}
											className={NetworkAlertsStyles.advanced_hide_input}
											placeholder='contains text...'
										/>
										<button
											type='button'
											className={NetworkAlertsStyles.advanced_hide_add_button}
											onClick={() => handleAddAdvancedHideRule('messageText')}
										>
											Add
										</button>
									</div>
								</div>

								<div className={NetworkAlertsStyles.advanced_hide_field}>
									<label className={NetworkAlertsStyles.advanced_hide_label} htmlFor='advanced-hide-src'>
										Source IP/Port
									</label>
									<div className={NetworkAlertsStyles.advanced_hide_input_row}>
										<input
											id='advanced-hide-src'
											type='text'
											value={advancedHideSrcIpPortInput}
											onChange={(e) => setAdvancedHideSrcIpPortInput(e.target.value)}
											className={NetworkAlertsStyles.advanced_hide_input}
											placeholder='e.g. 10.10.10.8:53'
										/>
										<button
											type='button'
											className={NetworkAlertsStyles.advanced_hide_add_button}
											onClick={() => handleAddAdvancedHideRule('srcIpPort')}
										>
											Add
										</button>
									</div>
								</div>
							</div>

							<div className={NetworkAlertsStyles.advanced_hide_rules_header}>
								<span className={NetworkAlertsStyles.advanced_hide_rules_title}>
									Active Rules ({advancedHideRuleCount})
								</span>
								<button
									type='button'
									className={NetworkAlertsStyles.advanced_hide_clear_button}
									onClick={handleClearAdvancedHideRules}
									disabled={!hasAdvancedHideRules}
								>
									Clear Rules
								</button>
							</div>

							{hasAdvancedHideRules ? (
								<div className={NetworkAlertsStyles.advanced_hide_rules_list}>
									{advancedHideRules.gidSidRev.map((ruleValue) => (
										<div key={`gid-${ruleValue}`} className={NetworkAlertsStyles.advanced_hide_rule_chip}>
											<span className={NetworkAlertsStyles.advanced_hide_rule_type}>GID/SID/REV</span>
											<span className={NetworkAlertsStyles.advanced_hide_rule_value}>{ruleValue}</span>
											<button
												type='button'
												className={NetworkAlertsStyles.advanced_hide_rule_remove}
												onClick={() => handleRemoveAdvancedHideRule('gidSidRev', ruleValue)}
												aria-label={`Remove advanced hide GID/SID/REV rule ${ruleValue}`}
											>
												×
											</button>
										</div>
									))}
									{advancedHideRules.messageText.map((ruleValue) => (
										<div key={`msg-${ruleValue}`} className={NetworkAlertsStyles.advanced_hide_rule_chip}>
											<span className={NetworkAlertsStyles.advanced_hide_rule_type}>Message</span>
											<span className={NetworkAlertsStyles.advanced_hide_rule_value}>{ruleValue}</span>
											<button
												type='button'
												className={NetworkAlertsStyles.advanced_hide_rule_remove}
												onClick={() => handleRemoveAdvancedHideRule('messageText', ruleValue)}
												aria-label={`Remove advanced hide message rule ${ruleValue}`}
											>
												×
											</button>
										</div>
									))}
									{advancedHideRules.srcIpPort.map((ruleValue) => (
										<div key={`src-${ruleValue}`} className={NetworkAlertsStyles.advanced_hide_rule_chip}>
											<span className={NetworkAlertsStyles.advanced_hide_rule_type}>Source</span>
											<span className={NetworkAlertsStyles.advanced_hide_rule_value}>{ruleValue}</span>
											<button
												type='button'
												className={NetworkAlertsStyles.advanced_hide_rule_remove}
												onClick={() => handleRemoveAdvancedHideRule('srcIpPort', ruleValue)}
												aria-label={`Remove advanced hide source rule ${ruleValue}`}
											>
												×
											</button>
										</div>
									))}
								</div>
							) : (
								<div className={NetworkAlertsStyles.advanced_hide_empty}>
									No advanced hide rules yet.
								</div>
							)}
						</div>
					)}

					{/* Header with Export Buttons */}
					<div className={NetworkAlertsStyles.table_header}>
						<h3 className={NetworkAlertsStyles.table_header_text}>{getPageTitle()}</h3>
						<div className={NetworkAlertsStyles.export_buttons}>
							<button
								className={NetworkAlertsStyles.export_button}
								onClick={() => {
									exportToCSV(filteredData, networkData.NetCols, getPageTitle());
								}}
							>
								Export CSV
							</button>
							<button
								className={NetworkAlertsStyles.export_button}
								onClick={() => {
									exportToPDF(filteredData, networkData.NetCols, getPageTitle());
								}}
							>
								Export PDF
							</button>
						</div>
					</div>

					<Table
						containerStyle={{ height: '60vh' }}
						tableHead={
							<tr>
								<th style={{ width: '5%', textAlign: 'center' }}>&nbsp;</th>
								{networkData.NetCols &&
									networkData.NetCols.length > 0 &&
									networkData.NetCols.map((col, index) =>
										// Notes use a modal; acknowledgement state styles the row.
										(col !== "Note" && !ALERT_HIDDEN_COLS.includes(col)) ? (
											<th 
												key={col + index}
												className={
													col === "Time Stamp" ? NetworkAlertsStyles.timestamp_header :
													col === "Watcher Name" ? NetworkAlertsStyles.watcher_header : ''
												}
											>
												{alertColumnLabel(col)}
											</th>
										) : null
									)}
								<th>Actions</th>
							</tr>
						}
						tableRow={
							networkTotalRows > 0 && networkRows.length > 0 ? (
								networkRows.map((rows, index) => {
									const alertKey = getNetworkAlertHideKey(rows);
									const isSelected = selectedNetworkAlertKeys.has(alertKey);
									const ackedIndex = (networkData.NetCols || []).indexOf('Acked');
									const isAcked = ackedIndex !== -1 && String(rows[ackedIndex] || '') !== '';
									return (
									<tr key={alertKey || index} className={`${isSelected ? NetworkAlertsStyles.selected_row : ''} ${isAcked ? NetworkAlertsStyles.acked_row : ''}`}>
										<td style={{ textAlign: 'center' }}>
											<input
												type='checkbox'
												className={NetworkAlertsStyles.alert_checkbox_row}
												checked={isSelected}
												onChange={(e) =>
													handleAlertSelectionChange(
														alertKey,
														e.target.checked,
														Boolean(e.nativeEvent?.shiftKey || e.shiftKey)
													)
												}
											/>
										</td>
										{rows && rows.length > 0 && 
											rows.map((row, colIndex) => {
												// Get column name from NetCols
												const colName = networkData.NetCols[colIndex];
												// Skip rendering the Note column in the table
												if (colName === "Note" || ALERT_HIDDEN_COLS.includes(colName)) return null;
												
												return (
													<td 
														key={row + colIndex}
														className={
															colName === "Time Stamp" ? NetworkAlertsStyles.timestamp_cell :
															colName === "Watcher Name" ? NetworkAlertsStyles.watcher_cell : ''
														}
													>
														{colName === 'Id' ? (
															<span className={NetworkAlertsStyles.alert_ids}>
																{String(row ?? '').split(',').join(', ')}
															</span>
														) : row}
													</td>
												);
											})
										}
										<td className={NetworkAlertsStyles.action_cell}>
											<div className={NetworkAlertsStyles.action_buttons_inline}>
												<button 
													className={NetworkAlertsStyles.view_note_button}
													onClick={() => handleViewNote(rows)}
												>
													View Note
												</button>
												{isAcked ? (
													<span className={NetworkAlertsStyles.acked_badge}>✓ Acked</span>
												) : (
												<>
													<button
														type='button'
														className={NetworkAlertsStyles.row_ack_button}
														onClick={() => handleAckSingleAlert(rows)}
														disabled={ackInFlight}
													>
														Ack
													</button>
													<button
														type='button'
														className={NetworkAlertsStyles.row_allow_button}
														onClick={() => handleAllowSingleAlert(rows)}
														disabled={ackInFlight}
														title='Acknowledge and allowlist so it will not alert again'
													>
														Ack & Allow
													</button>
												</>
												)}
												<button
													type='button'
													className={NetworkAlertsStyles.row_hide_button}
													onClick={() => handleHideSingleAlert(rows)}
												>
													Hide
												</button>
											</div>
										</td>
									</tr>
									);
								})
							) : (
								<tr>
									<td colSpan={(networkData.NetCols ? networkData.NetCols.filter(col => col !== "Note" && !ALERT_META_COLS.includes(col)).length : 0) + 2} style={{ textAlign: 'center' }}>
										{(rawNetworkRowsCount > 0 && (hiddenAlertCount > 0 || hasAdvancedHideRules))
											? `All ${rawNetworkRowsCount} alert${rawNetworkRowsCount === 1 ? '' : 's'} are hidden by hide filters. Use "Show Hidden", "Clear Rules", or "Reset Hide Filters".`
											: 'No data found'}
									</td>
								</tr>
							)
						}
					/>

					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={networkTotalRows}
							pageNumber={pageNumber}
						/>
					</div>
					
					{/* Note Modal */}
					{showNoteModal && selectedAlert && (
						<div className={NetworkAlertsStyles.modal_overlay}>
							<div className={NetworkAlertsStyles.modal_content}>
								<h3>Alert Note</h3>
								<p><strong>ID:</strong> {selectedAlert.time}</p>
								<p><strong>Time:</strong> {selectedAlert.time}</p>
								<p><strong>Watcher:</strong> {selectedAlert.watcher || "Unknown Watcher"}</p>
								<p><strong>Alert:</strong> {selectedAlert.alert}</p>
								<div className={NetworkAlertsStyles.note_content}>
									{selectedAlert.note || "No note has been added to this alert"}
								</div>
								<div className={NetworkAlertsStyles.modal_footer}>
									<button 
										className={NetworkAlertsStyles.close_button}
										onClick={handleCloseNoteModal}
									>
										Close
									</button>
								</div>
							</div>
						</div>
					)}

					{ackNotePrompt && (
						<div className={NetworkAlertsStyles.modal_overlay}>
							<div className={NetworkAlertsStyles.modal_content}>
								<h3>{ackNotePrompt.allow ? 'Acknowledge & Allow' : 'Acknowledge'}</h3>
								<p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
									{ackNotePrompt.count} alert{ackNotePrompt.count === 1 ? '' : 's'}
									{ackNotePrompt.allow ? ' - will be allowlisted (won’t re-alert)' : ''}
								</p>
								<textarea
									autoFocus
									className={NetworkAlertsStyles.ack_note_textarea}
									placeholder="Add a note (optional) - e.g. why this is expected"
									value={ackNoteText}
									onChange={(e) => setAckNoteText(e.target.value)}
								/>
								<div className={NetworkAlertsStyles.modal_footer}>
									<button className={NetworkAlertsStyles.close_button} onClick={() => setAckNotePrompt(null)}>Cancel</button>
									<button
										className={NetworkAlertsStyles.hide_control_button}
										disabled={ackInFlight}
										onClick={() => { const p = ackNotePrompt; setAckNotePrompt(null); ackAlertRows(p.rows, p.allow, ackNoteText); }}
									>
										{ackNotePrompt.allow ? 'Acknowledge & Allow' : 'Acknowledge'}
									</button>
								</div>
							</div>
						</div>
					)}
				</Fragment>
			)}
		</Content>
	);
};

export default NetworkAlertsScreen;
